import {AppUrls} from 'shared/config/model/urls';

import {useCallback, useState} from 'react';
import {t} from '@lingui/core/macro';

import {useMonitorDelete, useMonitorPause, useMonitorResume} from 'entities/monitor';
import {serverAPI} from 'features/server/api/serverAPI';
import {serverIncidentAPI} from 'features/server/api/serverIncidentAPI';
import {TypedDispatch} from 'shared/model/types';

interface IMutationParams {
    uuid: string,
    handleClose: () => void,
}

export const useServerPause = ({uuid, handleClose}: IMutationParams) => {
    const [pauseServer, {isLoading}] = serverAPI.usePauseServerMutation();

    return useMonitorPause({
        request: () => pauseServer(uuid).unwrap(),
        isLoading,
        handleClose,
        successText: t`Сервер поставлен на паузу`,
    });
};

export const useServerResume = ({uuid, handleClose}: IMutationParams) => {
    const [resumeServer, {isLoading}] = serverAPI.useResumeServerMutation();

    return useMonitorResume({
        request: () => resumeServer(uuid).unwrap(),
        isLoading,
        handleClose,
        successText: t`Сервер возобновлён`,
    });
};

const invalidateServerIncidents = (dispatch: TypedDispatch) => {
    dispatch(serverIncidentAPI.util.invalidateTags(['ServerIncident']));
};

export const useServerDelete = ({uuid, handleClose}: IMutationParams) => {
    const [deleteServer, {isLoading}] = serverAPI.useDeleteServerMutation();

    return useMonitorDelete({
        request: () => deleteServer(uuid).unwrap(),
        isLoading,
        handleClose,
        successText: t`Сервер удалён`,
        listUrl: AppUrls.SERVERS,
        detailsRoute: AppUrls.SERVER_DETAILS,
        onDeleted: invalidateServerIncidents,
    });
};

export const useServerRegenerateToken = ({uuid, handleClose}: IMutationParams) => {
    const [regenerateServerToken, {isLoading}] = serverAPI.useRegenerateServerTokenMutation();
    const [newToken, setNewToken] = useState<string | null>(null);

    const regenerate = useCallback(async() => {
        const result = await regenerateServerToken(uuid).unwrap();
        setNewToken(result.agent_token ?? '');
    }, [uuid, regenerateServerToken]);

    const closeAndReset = useCallback(() => {
        handleClose();
        setNewToken(null);
    }, [handleClose]);

    return {regenerate, closeAndReset, newToken, isLoading};
};
